import type { CalendarEventExternal } from '@schedule-x/calendar';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Temporal } from 'temporal-polyfill';
import { useCalendarDate } from '../../hooks/useCalendarDate';
import { useCalendarView } from '../../hooks/useCalendarView';
import { useSearchPreservingNavigate } from '../../hooks/useSearchPreservingNavigate';
import { trpc } from '../../lib/trpc';
import { Button } from '../../ui/button';
import { CreateTaskDialog } from '../tasks/CreateTaskDialog';
import { type CalendarEvent, CalendarView } from './CalendarView';
import { CalendarViewSwitcher } from './CalendarViewSwitcher';
import { UnscheduledTasksPanel } from './UnscheduledTasksPanel';

// dueAt is stored as the epoch-seconds of UTC midnight on the chosen calendar day (see
// CreateTaskDialog/TaskSideloadContent) — read/write via UTC getters so the date displayed
// here always matches the date the user picked, regardless of the browser's local timezone.
function dueAtToPlainDate(dueAt: number): Temporal.PlainDate {
  const d = new Date(dueAt * 1000);
  const iso = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  return Temporal.PlainDate.from(iso);
}

function dateToDueAtEpochSeconds(date: Date): number {
  const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return Math.floor(new Date(iso).getTime() / 1000);
}

export interface CalendarPageProps {
  /**
   * Connected workspace members for the create-task assignee picker. Passed in by the host app
   * because the two portals source members differently (clinic via REST, anchor via useHarbor).
   */
  workspaceMembers: Array<{ id: number; email: string; fullName: string | null }>;
}

export function CalendarPage({ workspaceMembers }: CalendarPageProps) {
  const navigate = useSearchPreservingNavigate();
  const { workspaceId, orgId } = useParams<{ workspaceId: string; orgId: string }>();

  const meQuery = trpc.auth.me.useQuery();
  const activeWorkspace = meQuery.data?.workspaces.find((w) => w.id.toString() === workspaceId);
  const isReadOnly = activeWorkspace?.role === 'viewer';

  const tasksQuery = trpc.tasks.getAll.useQuery();
  const statusesQuery = trpc.taskStatuses.list.useQuery();
  const tasks = tasksQuery.data || [];
  const { selectedDate, setSelectedDate } = useCalendarDate();
  const { view, setView } = useCalendarView();

  const [isCreateOpen, setIsCreateOpen] = React.useState(false);

  const scheduleMutation = trpc.tasks.update.useMutation({
    onSuccess: (_result, variables) => {
      toast.success('Task scheduled');
      tasksQuery.refetch();
      if (variables.dueAt != null) setSelectedDate(dueAtToPlainDate(variables.dueAt));
    },
    onError: (error) => {
      toast.error(`Failed to schedule task: ${error.message}`);
    },
  });

  const createMutation = trpc.tasks.create.useMutation({
    onSuccess: (_result, variables) => {
      toast.success('Task created');
      tasksQuery.refetch();
      setIsCreateOpen(false);
      if (variables.dueAt != null) setSelectedDate(dueAtToPlainDate(variables.dueAt));
    },
    onError: (error) => {
      toast.error(`Failed to create task: ${error.message}`);
    },
  });

  const scheduledTasks = React.useMemo(() => tasks.filter((t) => t.dueAt != null), [tasks]);
  const unscheduledTasks = React.useMemo(() => tasks.filter((t) => t.dueAt == null), [tasks]);

  const events: CalendarEvent[] = React.useMemo(
    () =>
      scheduledTasks.map((task) => {
        const date = dueAtToPlainDate(task.dueAt!);
        const member = task.assignedTo
          ? workspaceMembers.find((m) => m.id.toString() === task.assignedTo?.toString())
          : null;
        return {
          id: task.id,
          title: task.label,
          start: date,
          end: date,
          assignedUser: member,
        };
      }),
    [scheduledTasks, workspaceMembers]
  );

  const goToTask = (taskId: number) => {
    navigate(`/org/${orgId}/workspace/${workspaceId}/tasks/${taskId}`);
  };

  const handleEventClick = (event: CalendarEventExternal) => {
    const taskId = typeof event.id === 'number' ? event.id : parseInt(String(event.id), 10);
    if (!Number.isNaN(taskId)) goToTask(taskId);
  };

  const handleSchedule = (taskId: number, date: Date) => {
    if (isReadOnly) return;
    scheduleMutation.mutate({ id: taskId, dueAt: dateToDueAtEpochSeconds(date) });
  };

  return (
    <main className="flex-1 flex flex-col h-full w-full bg-slate-50/50 dark:bg-slate-950/50 overflow-hidden">
      <div className="p-6 pb-4 shrink-0 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Calendar</h1>
          <p className="text-muted-foreground mt-1">
            Tasks with a due date show up here. Schedule unassigned tasks from the sidebar.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <CalendarViewSwitcher value={view} onChange={setView} />
          {!isReadOnly && (
            <Button className="w-full md:w-auto" onClick={() => setIsCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Task
            </Button>
          )}
        </div>
      </div>
      <div className="flex-1 flex min-h-0 px-6 pb-6 gap-6">
        <div className="flex-1 min-w-0">
          <CalendarView
            key={`${view}-${selectedDate?.toString() ?? 'today'}`}
            events={events}
            defaultView={view}
            selectedDate={selectedDate}
            onSelectedDateUpdate={setSelectedDate}
            onEventClick={handleEventClick}
          />
        </div>
        <UnscheduledTasksPanel
          tasks={unscheduledTasks}
          workspaceMembers={workspaceMembers}
          statuses={statusesQuery.data?.items ?? []}
          isLoading={tasksQuery.isLoading}
          isReadOnly={isReadOnly}
          onTaskClick={goToTask}
          onSchedule={handleSchedule}
        />
      </div>

      <CreateTaskDialog
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={(data) => createMutation.mutate(data)}
        initialTitle=""
        initialDescription=""
        workspaceMembers={workspaceMembers}
        isSubmitting={createMutation.isPending}
        requireDueDate
      />
    </main>
  );
}
